import type { ConditionAlertModel } from '.';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type { ConditionAlertDefinition } from '@thinkalpha/platform-ws-client/contracts/alerts.js';
import { injectable, inject } from 'src/features/ioc';
import { instrumentToString } from 'src/lib/instrumentUtils';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Implementation of ConditionAlertModel.
 * Manages condition (symbol) alert definition and triggered state.
 */
@injectable()
export class ConditionAlertModelImpl implements ConditionAlertModel, Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #definition = signal<ConditionAlertDefinition>(null!);
    readonly #isTriggered = signal(false);

    #id: string = '';
    #instrument: Instrument = null!;
    #initialized = false;

    constructor(@inject('Logger') private logger: ReactBindings['Logger']) {
        this.logger = logger.getSubLogger({ name: 'ConditionAlertModel' });
    }

    get id(): string {
        return this.#id;
    }

    get instrument(): Instrument {
        return this.#instrument;
    }

    get definition(): ReadonlySignal<ConditionAlertDefinition> {
        return this.#definition;
    }

    get isTriggered(): ReadonlySignal<boolean> {
        return this.#isTriggered;
    }

    /**
     * Initialize the model from a condition alert definition.
     */
    initFromDefinition(definition: ConditionAlertDefinition): void {
        if (this.#initialized) {
            this.logger.warn('ConditionAlertModel already initialized', { alertId: this.#id });
            return;
        }
        this.#initialized = true;

        this.#id = definition.id;
        this.#instrument = definition.instrument;
        this.#definition.value = definition;

        if (TRACE) {
            this.logger.trace('ConditionAlertModel initialized', {
                alertId: this.#id,
                instrument: instrumentToString(definition.instrument),
            });
        }
    }

    /**
     * Update the model with a new definition (e.g., when alert is modified).
     */
    updateDefinition(definition: ConditionAlertDefinition): void {
        this.#definition.value = definition;
    }

    /**
     * Set the triggered state (called when worker reports state change).
     */
    setTriggered(value: boolean): void {
        this.#isTriggered.value = value;
    }

    readonly [Symbol.dispose] = () => this.#disposableStack.dispose();
}
